/**
 * Data layer for `customizable-table-columns-per-view`. Thin typed fetch wrappers over /api/v1/customizable-table-columns-per-view.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CustomizableTableColumnsPerViewRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCustomizableTableColumnsPerViewResponse {
	count: number;
	items: CustomizableTableColumnsPerViewRow[];
}

export interface CreateCustomizableTableColumnsPerViewBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/customizable-table-columns-per-view";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCustomizableTableColumnsPerViewResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCustomizableTableColumnsPerViewResponse>(res);
}

export async function create(body: CreateCustomizableTableColumnsPerViewBody): Promise<CustomizableTableColumnsPerViewRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CustomizableTableColumnsPerViewRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const customizableTableColumnsPerViewQueryKeys = {
	all: ["customizable-table-columns-per-view"] as const,
	list: () => [...customizableTableColumnsPerViewQueryKeys.all] as const,
	detail: (id: string) => [...customizableTableColumnsPerViewQueryKeys.all, id] as const,
};
